import React, { useMemo, useCallback, useState } from "react";
import { Collapse } from "react-collapse";
import SearchBar from "../../atoms/searchBar/SearchBar.jsx";
import EventCard from "../eventCard/EventCard.jsx";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButton from "@mui/material/IconButton";
import Loader from "../../atoms/loader/Loader.jsx";
import "./UserHistoricEventsPage.css";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../confirmationModal/ConfiramationModal.jsx";
import Checkbox from "@mui/material/Checkbox";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
const UserHistoricEventsPage = ({
  openSection,
  isSmallScreen,
  userHistoricEventsList,
  handleEventSelect,
  loadingEventId,
  onDeselectAllEvents,
  onLoadMore,
  historicSearchReference = "",
  onChangeHistoricSearchReference,
  onDeleteEvent,
}) => {
  const { t } = useTranslation();
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const toggleChecked = useCallback((id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const selectedCount = userHistoricEventsList.selectedEventIds?.length ?? 0;
  const { hasMore, isLoadingMore, total } = userHistoricEventsList;
  const hasActiveFilters = Boolean(historicSearchReference?.trim());

  const handleScroll = useCallback(
    (e) => {
      if (!onLoadMore || !hasMore || isLoadingMore) return;
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop - clientHeight < 150) {
        onLoadMore();
      }
    },
    [onLoadMore, hasMore, isLoadingMore],
  );

  const convertToHectares = useCallback((areaM2) => {
    if (areaM2 === null || areaM2 === undefined) return "0";
    const hectares = areaM2 / 10000;
    return hectares.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(",", " à");
  }, []);

  const formatDateShort = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }, []);

  // Defensive client-side sort only — actual filtering now happens
  // server-side (search_fires / get_myfires), driven by
  // historicSearchReference in MapScreen.
  const sortedData = useMemo(() => {
    if (!Array.isArray(userHistoricEventsList.data)) return [];
    return [...userHistoricEventsList.data].sort((a, b) => {
      const dateA = new Date(a.fire.created_at ?? 0);
      const dateB = new Date(b.fire.created_at ?? 0);
      return dateB - dateA;
    });
  }, [userHistoricEventsList.data]);
  // only ids that are still in the list (e.g. after a search change)
  const idsToDelete = useMemo(
    () => sortedData.map((i) => i.fire.id).filter((id) => checkedIds.has(id)),
    [sortedData, checkedIds],
  );

  const handleConfirmDelete = async () => {
  setConfirmOpen(false);
  if (!onDeleteEvent || idsToDelete.length === 0) return;
  const count = idsToDelete.length;
  const ok = await onDeleteEvent(idsToDelete);
  if (ok) {
    setCheckedIds(new Set());
    setToast({
      open: true,
      severity: "success",
      message: t("userHistoricEvents.deleteSuccess", {
        count,
        defaultValue: "{{count}} event(s) deleted successfully",
      }),
    });
  } else {
    setToast({
      open: true,
      severity: "error",
      message: t("userHistoricEvents.deleteError", {
        defaultValue: "Failed to delete the selected events",
      }),
    });
  }
};
  const listBody = userHistoricEventsList.isLoading ? (
    <div
      className="historic-events-loading"
      data-testid="historic-events-loading"
    >
      <Loader size={isSmallScreen ? 10 : 20} />
    </div>
  ) : sortedData.length === 0 && hasActiveFilters ? (
    <div className="no-results" data-testid="historic-events-no-results">
      <p>
        {t("userHistoricEvents.noResults", { term: historicSearchReference })}
      </p>
    </div>
  ) : (
    sortedData.map((item, index) => (
      <div className="event-card-wrapper" key={item.fire.id ?? index}>
        <EventCard
          data-testid="historic-event-card"
          item={item}
          isSelected={userHistoricEventsList.selectedEventIds.includes(
            item.fire.id,
          )}
          isLoading={loadingEventId === item.fire.id}
          onSelect={handleEventSelect}
          formatDate={formatDate}
          formatDateShort={formatDateShort}
          convertToHectares={convertToHectares}
        />
        <Checkbox
          className="event-check"
          size="small"
          icon={<RadioButtonUncheckedIcon />}
          checkedIcon={<CheckCircleIcon />}
          checked={checkedIds.has(item.fire.id)}
          onChange={() => toggleChecked(item.fire.id)}
          onClick={(e) => e.stopPropagation()}
          inputProps={{ "aria-label": t("userHistoricEvents.eventCard.check") }}
          data-testid="historic-event-checkbox"
        />
      </div>
    ))
  );

  const eventListJSX = (
    <div
      className="events-list-container"
      data-testid="historic-events-list-container"
    >
    <div className="events-toolbar">
  <div className="events-search">
    <SearchBar
      searchQuery={historicSearchReference}
      setSearchQuery={onChangeHistoricSearchReference}
      resultCount={total}
      placeholder={t("userHistoricEvents.searchPlaceholder")}
      data-testid="historic-events-search-bar"
    />
  </div>
  <div className="events-actions">
    <button
      type="button"
      className="deselect-all-btn"
      onClick={onDeselectAllEvents}
      disabled={selectedCount === 0}
      data-testid="historic-events-deselect-all"
    >
      {t("userHistoricEvents.deselectAll")}
      {selectedCount > 0 ? ` (${selectedCount})` : ""}
    </button>
    <IconButton
      size="small"
      className="delete-selected-btn"
      disabled={idsToDelete.length === 0}
      onClick={() => setConfirmOpen(true)}
      aria-label={t("userHistoricEvents.eventCard.delete")}
      data-testid="historic-events-delete-selected"
    >
      <DeleteOutlineIcon fontSize="small" />
    </IconButton>
  </div>
</div>
      <div
        className={`events-scrollable ${isSmallScreen ? "events-scrollable-mobile" : ""}`}
        data-testid="historic-events-scrollable"
        onScroll={handleScroll}
      >
        {listBody}
        {isLoadingMore && (
          <div
            className="historic-events-loading-more"
            data-testid="historic-events-loading-more"
          >
            <Loader size={10} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {isSmallScreen ? (
        <Collapse isOpened={openSection === "eventsHistoric"}>
          {eventListJSX}
        </Collapse>
      ) : (
        <div
          className="user-historic-events-page"
          data-testid="historic-events-desktop-container"
        >
          {eventListJSX}
        </div>
      )}
      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t("userHistoricEvents.eventCard.deleteTitle")}
        message={t("userHistoricEvents.eventCard.deleteMessage", {
          count: idsToDelete.length,
        })}
      />
      <Snackbar
  open={toast.open}
  autoHideDuration={4000}
  onClose={() => setToast((p) => ({ ...p, open: false }))}
  anchorOrigin={{ vertical: "top", horizontal: "center" }}
>
  <Alert
    severity={toast.severity}
    variant="filled"
    onClose={() => setToast((p) => ({ ...p, open: false }))}
  >
    {toast.message}
  </Alert>
</Snackbar>
    </>
  );
};

export default UserHistoricEventsPage;
